import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useLibraryProfile } from '../../hooks/useLibraryProfile'
import { useAuthStore } from '../../store/authStore'
import ProfileForm from '../../components/library/ProfileForm'
import DeleteAccountSection from '../../components/library/DeleteAccountSection'
import UiButton from '../../components/ui/UiButton'

/**
 * Profil page.
 *
 * Read-only profile sections with per-section editing, account deletion,
 * and logout.
 */
export default function LibraryProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const { profile, loading, error, saving, deleting, saveProfile, removeAccount } =
    useLibraryProfile()

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  if (loading) {
    return (
      <div className="bg-[var(--library-card-bg)] backdrop-blur-md rounded-[2rem] p-6 shadow-xl border border-[var(--library-border)] min-h-[400px]">
        <h2 className="font-climate text-2xl text-brand-blue mb-4">
          {t('library.profile.title')}
        </h2>
        <p className="text-base text-brand-blue/80">{t('common.loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-[var(--library-card-bg)] backdrop-blur-md rounded-[2rem] p-6 shadow-xl border border-[var(--library-border)] min-h-[400px]">
        <h2 className="font-climate text-2xl text-brand-blue mb-4">
          {t('library.profile.title')}
        </h2>
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  return (
    <div className="bg-[var(--library-card-bg)] backdrop-blur-md rounded-[2rem] p-6 md:p-10 shadow-xl border border-[var(--library-border)] min-h-[400px]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h2 className="font-climate text-2xl text-brand-blue">
          {t('library.profile.title')}
        </h2>
        <UiButton
          type="button"
          variant="secondary"
          size="sm"
          icon={LogOut}
          onClick={handleLogout}
        >
          {t('nav.logout')}
        </UiButton>
      </div>

      <div className="max-w-3xl mx-auto">
        <ProfileForm profile={profile} saving={saving} onSave={saveProfile} />
        <DeleteAccountSection deleting={deleting} onDelete={removeAccount} />
      </div>
    </div>
  )
}
